import { ATTRIBUTE_TYPES } from 'Constants/attributes.constants'
import ModalComp from 'Components/common/Modal/Modal'
import TextAreaAttribute from 'Components/attributes/TextAreaAttribute/TextArea'
import type { CSATFeedbackData } from 'Types/CSAT.types'
import styles from './RejectOrSkipModal.module.less'
import Button from 'antd/lib/button'
import Form from 'antd/lib/form'
import Select from 'antd/lib/select'
import Tooltip from 'antd/lib/tooltip'
import { TaskMode } from 'Types/Tasks.types'
import { useCallback, useState } from 'react'
import { RejectOrSkipModalMetaData } from 'Core/ModalCore/Modals/RejectOrSkipModalController'
import { useMessage } from 'shared/hooks/use-message'
import { useCSATFeedback } from 'Hooks/use-csat-feedback'

type RejectOrSkipFormValues = {
    skipReasons?: string[]
    rejectionReasons?: string[]
}

export type RejectOrSkipModalProps = {
    getVisibility: () => boolean
    getMetaData: () => RejectOrSkipModalMetaData
    onConfirm: (
        action?: 'submit' | 'dismiss',
        payload?: {
            comment: string
            skipReasons?: string[]
            rejectionReasons?: string[]
            feedbackData?: CSATFeedbackData
        }
    ) => void
    onCancel: () => void
}

export const RejectOrSkipModal: React.FC<RejectOrSkipModalProps> = props => {
    const [skipModalValue, setSkipModalValue] = useState<string | undefined>('')
    const [form] = Form.useForm<RejectOrSkipFormValues>()
    const metaData = props.getMetaData()
    const feedbackConfig = metaData?.feedbackConfig
    const { csatSurveyUI, resetCSATFeedback, getFeedbackData } = useCSATFeedback(feedbackConfig)
    const modalHeader = metaData?.isSkip ? 'Skip Task?' : 'Reject Task?'
    const [messageApi] = useMessage()
    const isCommentMandatory = !metaData?.isSkip

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()
            if (isCommentMandatory && !skipModalValue?.trim()) {
                messageApi.error('Please add a comment before rejecting')
                return
            }
            const skipReasons = values.skipReasons?.length ? values.skipReasons : undefined
            const rejectionReasons = values.rejectionReasons?.length
                ? values.rejectionReasons
                : undefined

            let feedbackData: CSATFeedbackData | undefined
            if (metaData?.isSkip) {
                const result = getFeedbackData()
                if (result.error) {
                    messageApi.error(result.error)
                    return
                }
                feedbackData = result.feedbackData
            }

            props.onConfirm('submit', {
                comment: skipModalValue ?? '',
                skipReasons,
                rejectionReasons,
                feedbackData,
            })
        } catch (error) {
            messageApi.error('Please fill in all required fields')
        }
    }

    const handleCancel = useCallback(() => {
        setSkipModalValue('')
        form.resetFields()
        resetCSATFeedback()
        props.onCancel()
    }, [props, form, resetCSATFeedback])

    const getModalContent = () => {
        const isSkip = metaData?.isSkip
        const taskMode = metaData?.taskMode

        if (isSkip) {
            return "On skipping, You'll be redirected to the next task. Current task will no longer be available."
        }

        if (taskMode === TaskMode.Triager) {
            return "The task will end here and won't be taken up further. Please provide your reason for rejecting."
        }

        return 'The task will be sent back to expert for correction. Add your feedback for the expert.'
    }

    const renderReasonSelector = useCallback(
        (
            isSkip: boolean,
            reasons: string[] | undefined,
            isOptional: boolean,
            fieldName: string,
            label: string,
            placeholder: string
        ) => {
            const hasReasons = !!reasons?.length
            if (!hasReasons) return null

            return (
                <Form.Item
                    name={fieldName}
                    label={label}
                    className={styles['form-item']}
                    rules={
                        !isOptional
                            ? [
                                  {
                                      required: true,
                                      message: `Please select at least one ${
                                          isSkip ? 'skip' : 'rejection'
                                      } reason`,
                                  },
                              ]
                            : undefined
                    }
                >
                    <Select
                        mode="multiple"
                        maxTagCount={2}
                        maxTagPlaceholder={omittedValues => (
                            <Tooltip
                                title={omittedValues.map(({ label }) => label).join(', ')}
                                rootClassName={styles['white-tooltip']}
                            >
                                <span>+{omittedValues.length} more</span>
                            </Tooltip>
                        )}
                        options={
                            reasons?.map(reason => ({
                                label: reason,
                                value: reason,
                            })) ?? []
                        }
                        filterOption={(input, option) =>
                            !!option?.label.toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder={placeholder}
                        popupMatchSelectWidth={false}
                        allowClear
                        className={styles['reason-select']}
                        popupClassName={styles['reason-select-dropdown']}
                    />
                </Form.Item>
            )
        },
        []
    )

    return (
        <div data-testid="reject-skip-modal-wrapper">
            <ModalComp
                visible={props.getVisibility()}
                centered
                className={styles.rejectOrSkipModal}
                modalHeader={<div className={'modal-header'}>{modalHeader}</div>}
                modalFooter={
                    <div className={'modal-button'}>
                        <Button onClick={handleCancel}>Cancel</Button>
                        <Button type="primary" onClick={handleSubmit}>
                            Confirm
                        </Button>
                    </div>
                }
                onCancel={handleCancel}
            >
                <div className={styles['textarea-outer']}>
                    <span>{getModalContent()}</span>
                    <Form form={form} layout="vertical" requiredMark>
                        {metaData?.isSkip &&
                            renderReasonSelector(
                                true,
                                metaData.skipReasons,
                                metaData.isSkipReasonSelectionOptional ?? false,
                                'skipReasons',
                                'Skip Reason',
                                'Select skip reasons...'
                            )}
                        {!metaData?.isSkip &&
                            renderReasonSelector(
                                false,
                                metaData.rejectionReasons,
                                metaData.isRejectionReasonSelectionOptional ?? false,
                                'rejectionReasons',
                                'Rejection Reason',
                                'Select rejection reasons...'
                            )}
                        <div className={'modal-textarea-parent'}>
                            <TextAreaAttribute
                                className={'modal-textarea'}
                                type={ATTRIBUTE_TYPES.TEXTAREA}
                                rows={6}
                                placeholder={
                                    isCommentMandatory
                                        ? 'Additional Comments (required)...'
                                        : 'Additional Comments...'
                                }
                                id={'modal'}
                                width="100%"
                                value={skipModalValue}
                                onChange={setSkipModalValue}
                            />
                        </div>

                        {metaData?.isSkip && csatSurveyUI}
                    </Form>
                </div>
            </ModalComp>
        </div>
    )
}
